import { get, post, put } from 'data/services/apiService';
import { API_URL } from 'data/services/apiConstants';

// Email Preferences Action Types
export const EMAIL_PREFERENCES_REQUEST = 'EMAIL_PREFERENCES_REQUEST';
export const EMAIL_PREFERENCES_SUCCESS = 'EMAIL_PREFERENCES_SUCCESS';
export const EMAIL_PREFERENCES_ERROR = 'EMAIL_PREFERENCES_ERROR';

export const VERIFY_TOKEN_REQUEST = 'VERIFY_TOKEN_REQUEST';
export const VERIFY_TOKEN_SUCCESS = 'VERIFY_TOKEN_SUCCESS';
export const VERIFY_TOKEN_ERROR = 'VERIFY_TOKEN_ERROR';

export const UPDATE_PREFERENCES_REQUEST = 'UPDATE_PREFERENCES_REQUEST';
export const UPDATE_PREFERENCES_SUCCESS = 'UPDATE_PREFERENCES_SUCCESS';
export const UPDATE_PREFERENCES_ERROR = 'UPDATE_PREFERENCES_ERROR';

export const SUBMIT_FEEDBACK_REQUEST = 'SUBMIT_FEEDBACK_REQUEST';
export const SUBMIT_FEEDBACK_SUCCESS = 'SUBMIT_FEEDBACK_SUCCESS';
export const SUBMIT_FEEDBACK_ERROR = 'SUBMIT_FEEDBACK_ERROR';

export const GENERATE_TOKEN_REQUEST = 'GENERATE_TOKEN_REQUEST';
export const GENERATE_TOKEN_SUCCESS = 'GENERATE_TOKEN_SUCCESS';
export const GENERATE_TOKEN_ERROR = 'GENERATE_TOKEN_ERROR';

export const CLEAR_EMAIL_PREFERENCES_STATE = 'CLEAR_EMAIL_PREFERENCES_STATE';

// Initial State
const initialState = {
  preferences: null,
  user: null,
  token: null,
  properties: [],
  isLoading: false,
  isVerifying: false,
  isUpdating: false,
  isSubmitting: false,
  isGenerating: false,
  isFetchingProperties: false,
  isUpdatingProperties: false,
  error: null,
  success: null,
  // null = not yet verified (prevents invalid-link flash on first render)
  tokenValid: null,
};

// Reducer
export default function emailPreferencesReducer(state = initialState, action) {
  switch (action.type) {
    // Verify Token
    case VERIFY_TOKEN_REQUEST:
      return {
        ...state,
        isVerifying: true,
        tokenValid: null,
        error: null,
      };
    case VERIFY_TOKEN_SUCCESS:
      return {
        ...state,
        isVerifying: false,
        user: action.payload?.data?.user,
        tokenValid: action.payload?.data?.token_valid,
        error: null,
      };
    case VERIFY_TOKEN_ERROR:
      return {
        ...state,
        isVerifying: false,
        tokenValid: false,
        error: action.payload,
      };

    // Get Preferences
    case EMAIL_PREFERENCES_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case EMAIL_PREFERENCES_SUCCESS:
      return {
        ...state,
        isLoading: false,
        preferences: action.payload?.data?.preferences,
        error: null,
      };
    case EMAIL_PREFERENCES_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    // Update Preferences
    case UPDATE_PREFERENCES_REQUEST:
      return {
        ...state,
        isUpdating: true,
        error: null,
        success: null,
      };
    case UPDATE_PREFERENCES_SUCCESS:
      return {
        ...state,
        isUpdating: false,
        success: action.payload?.message || action.payload?.data?.message || 'Preferences updated successfully',
        error: null,
      };
    case UPDATE_PREFERENCES_ERROR:
      return {
        ...state,
        isUpdating: false,
        error: action.payload,
        success: null,
      };

    // Submit Feedback
    case SUBMIT_FEEDBACK_REQUEST:
      return {
        ...state,
        isSubmitting: true,
        error: null,
        success: null,
      };
    case SUBMIT_FEEDBACK_SUCCESS:
      return {
        ...state,
        isSubmitting: false,
        success: action.payload?.message || action.payload?.data?.message || 'Feedback submitted successfully',
        error: null,
      };
    case SUBMIT_FEEDBACK_ERROR:
      return {
        ...state,
        isSubmitting: false,
        error: action.payload,
        success: null,
      };

    // Generate Token
    case GENERATE_TOKEN_REQUEST:
      return {
        ...state,
        isGenerating: true,
        error: null,
      };
    case GENERATE_TOKEN_SUCCESS:
      return {
        ...state,
        isGenerating: false,
        token: action.payload?.data?.token,
        error: null,
      };
    case GENERATE_TOKEN_ERROR:
      return {
        ...state,
        isGenerating: false,
        error: action.payload,
      };

    // Fetch Properties
    case FETCH_PROPERTIES_REQUEST:
      return {
        ...state,
        isFetchingProperties: true,
        tokenValid: null,
        error: null,
      };
    case FETCH_PROPERTIES_SUCCESS:
      return {
        ...state,
        isFetchingProperties: false,
        properties: action.payload?.data?.properties,
        user: action.payload?.data?.user,
        tokenValid: true,
        error: null,
      };
    case FETCH_PROPERTIES_ERROR:
      return {
        ...state,
        isFetchingProperties: false,
        error: action.payload,
        tokenValid: false,
      };

    // Update Property Notifications
    case UPDATE_PROPERTY_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        isUpdatingProperties: true,
        error: null,
      };
    case UPDATE_PROPERTY_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        isUpdatingProperties: false,
        success: action.payload?.message || action.payload?.data?.message || 'Preferences updated successfully',
        error: null,
      };
    case UPDATE_PROPERTY_NOTIFICATIONS_ERROR:
      return {
        ...state,
        isUpdatingProperties: false,
        error: action.payload,
      };

    // Clear State
    case CLEAR_EMAIL_PREFERENCES_STATE:
      return initialState;

    default:
      return state;
  }
}

// Action Creators
export const clearEmailPreferencesState = () => ({
  type: CLEAR_EMAIL_PREFERENCES_STATE,
});

export const verifyTokenRequest = () => ({
  type: VERIFY_TOKEN_REQUEST,
});

export const verifyTokenSuccess = (data) => ({
  type: VERIFY_TOKEN_SUCCESS,
  payload: data,
});

export const verifyTokenError = (error) => ({
  type: VERIFY_TOKEN_ERROR,
  payload: error,
});

export const emailPreferencesRequest = () => ({
  type: EMAIL_PREFERENCES_REQUEST,
});

export const emailPreferencesSuccess = (data) => ({
  type: EMAIL_PREFERENCES_SUCCESS,
  payload: data,
});

export const emailPreferencesError = (error) => ({
  type: EMAIL_PREFERENCES_ERROR,
  payload: error,
});

export const updatePreferencesRequest = () => ({
  type: UPDATE_PREFERENCES_REQUEST,
});

export const updatePreferencesSuccess = (data) => ({
  type: UPDATE_PREFERENCES_SUCCESS,
  payload: data,
});

export const updatePreferencesError = (error) => ({
  type: UPDATE_PREFERENCES_ERROR,
  payload: error,
});

export const submitFeedbackRequest = () => ({
  type: SUBMIT_FEEDBACK_REQUEST,
});

export const submitFeedbackSuccess = (data) => ({
  type: SUBMIT_FEEDBACK_SUCCESS,
  payload: data,
});

export const submitFeedbackError = (error) => ({
  type: SUBMIT_FEEDBACK_ERROR,
  payload: error,
});

export const generateTokenRequest = () => ({
  type: GENERATE_TOKEN_REQUEST,
});

export const generateTokenSuccess = (data) => ({
  type: GENERATE_TOKEN_SUCCESS,
  payload: data,
});

export const generateTokenError = (error) => ({
  type: GENERATE_TOKEN_ERROR,
  payload: error,
});

const INVALID_LINK_MESSAGE = 'Looks like this link is invalid or has expired.';

const parsePublicEmailPreferencesResponse = async (response, fallbackMessage) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || fallbackMessage);
    }

    return result;
  }

  if (response.status === 404) {
    throw new Error(INVALID_LINK_MESSAGE);
  }

  throw new Error(fallbackMessage);
};

// Thunk Actions

/**
 * Verify unsubscribe token (public - no auth)
 */
export const verifyUnsubscribeToken = (token) => (dispatch) => {
  dispatch(verifyTokenRequest());

  return fetch(`${API_URL}/api/email-preferences/verify/${token}`)
    .then(response => parsePublicEmailPreferencesResponse(response, 'Failed to verify token'))
    .then(result => {
      dispatch(verifyTokenSuccess(result));
      return result.data;
    })
    .catch(error => {
      const errorMessage = error.message || INVALID_LINK_MESSAGE;
      dispatch(verifyTokenError(errorMessage));
      throw error;
    });
};

/**
 * Get user email preferences (authenticated)
 */
export const fetchEmailPreferences = () => (dispatch, getState) => {
  const state = getState();
  const url = `${API_URL}/api/email-preferences`;

  dispatch(emailPreferencesRequest());

  return get({
    dispatch,
    url,
    authToken: state.user.authToken,
    onSuccess: (result) => {
      dispatch(emailPreferencesSuccess(result));
      return result;
    },
    onError: (error) => {
      dispatch(emailPreferencesError(error.message || 'Failed to load preferences'));
      throw error;
    },
  });
};

/**
 * Update email preferences (authenticated)
 */
export const updateEmailPreferences = (data) => (dispatch, getState) => {
  const state = getState();
  const url = `${API_URL}/api/email-preferences`;

  dispatch(updatePreferencesRequest());

  return put({
    dispatch,
    url,
    data,
    authToken: state.user.authToken,
    onSuccess: (result) => {
      dispatch(updatePreferencesSuccess(result));
      return result;
    },
    onError: (error) => {
      dispatch(updatePreferencesError(error.message || 'Failed to update preferences'));
      throw error;
    },
  });
};

/**
 * Update email preferences with token (public - no auth)
 */
export const updatePreferencesWithToken = (token, data) => (dispatch) => {
  dispatch(updatePreferencesRequest());

  return fetch(`${API_URL}/api/email-preferences/update/${token}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
    .then(response => parsePublicEmailPreferencesResponse(response, 'Failed to update preferences'))
    .then(result => {
      dispatch(updatePreferencesSuccess(result));
      return result.data;
    })
    .catch(error => {
      const errorMessage = error.message || INVALID_LINK_MESSAGE;
      dispatch(updatePreferencesError(errorMessage));
      throw error;
    });
};

/**
 * Submit unsubscribe feedback (public - no auth)
 */
export const submitUnsubscribeFeedback = (token, feedbackData) => (dispatch) => {
  dispatch(submitFeedbackRequest());

  return fetch(`${API_URL}/api/email-preferences/feedback/${token}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(feedbackData),
  })
    .then(response => parsePublicEmailPreferencesResponse(response, 'Failed to submit feedback'))
    .then(result => {
      dispatch(submitFeedbackSuccess(result));
      return result.data;
    })
    .catch(error => {
      const errorMessage = error.message || INVALID_LINK_MESSAGE;
      dispatch(submitFeedbackError(errorMessage));
      throw error;
    });
};

/**
 * Generate new unsubscribe token (authenticated)
 */
export const generateUnsubscribeToken = () => (dispatch, getState) => {
  const state = getState();
  const url = `${API_URL}/api/email-preferences/generate-token`;

  dispatch(generateTokenRequest());

  return post({
    dispatch,
    url,
    authToken: state.user.authToken,
    onSuccess: (result) => {
      dispatch(generateTokenSuccess(result));
      return result;
    },
    onError: (error) => {
      dispatch(generateTokenError(error.message || 'Failed to generate token'));
      throw error;
    },
  });
};

// Property Notifications Action Types
export const FETCH_PROPERTIES_REQUEST = 'FETCH_PROPERTIES_REQUEST';
export const FETCH_PROPERTIES_SUCCESS = 'FETCH_PROPERTIES_SUCCESS';
export const FETCH_PROPERTIES_ERROR = 'FETCH_PROPERTIES_ERROR';

export const UPDATE_PROPERTY_NOTIFICATIONS_REQUEST = 'UPDATE_PROPERTY_NOTIFICATIONS_REQUEST';
export const UPDATE_PROPERTY_NOTIFICATIONS_SUCCESS = 'UPDATE_PROPERTY_NOTIFICATIONS_SUCCESS';
export const UPDATE_PROPERTY_NOTIFICATIONS_ERROR = 'UPDATE_PROPERTY_NOTIFICATIONS_ERROR';

/**
 * Fetch user's properties with notification settings
 */
export const fetchPropertiesWithNotifications = (token) => (dispatch) => {
  dispatch({ type: FETCH_PROPERTIES_REQUEST });

  return fetch(`${API_URL}/api/email-preferences/properties/${token}`)
    .then(response => parsePublicEmailPreferencesResponse(response, 'Failed to fetch properties'))
    .then(result => {
      dispatch({
        type: FETCH_PROPERTIES_SUCCESS,
        payload: result
      });
      return result.data;
    })
    .catch(error => {
      const errorMessage = error.message || INVALID_LINK_MESSAGE;
      dispatch({
        type: FETCH_PROPERTIES_ERROR,
        payload: errorMessage
      });
      throw error;
    });
};

/**
 * Update property notification preferences
 */
export const updatePropertyNotifications = (token, propertyNotifications) => (dispatch) => {
  dispatch({ type: UPDATE_PROPERTY_NOTIFICATIONS_REQUEST });

  return fetch(`${API_URL}/api/email-preferences/properties/${token}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ property_notifications: propertyNotifications }),
  })
    .then(response => parsePublicEmailPreferencesResponse(response, 'Failed to update notifications'))
    .then(result => {
      dispatch({
        type: UPDATE_PROPERTY_NOTIFICATIONS_SUCCESS,
        payload: result
      });
      return result.data || result.message;
    })
    .catch(error => {
      const errorMessage = error.message || INVALID_LINK_MESSAGE;
      dispatch({
        type: UPDATE_PROPERTY_NOTIFICATIONS_ERROR,
        payload: errorMessage
      });
      throw error;
    });
};